import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  ArrowLeft, FileText, Save, ScrollText, Loader2, Layers, Lock, Sparkles, Upload, Trash2,
} from 'lucide-react';
import { usePortalI18n } from '../context/PortalI18nContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─────────────────────────────────────────────────────────────────────────
// Grant Policy detail page.
//
// One component, two routes:
//   • /grant-policies/:id        — admin only, fully editable
//   • /portal/policies/:id       — secretary, read-only
//
// The `mode` prop tells which surface we're rendering on. The backend
// already enforces auth: portal/* routes 404 the secretary out of any
// status='closed' policies, and PUT requires super-admin.
// ─────────────────────────────────────────────────────────────────────────

const prettifyDocType = (slug) =>
  String(slug || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const GrantPolicyDetail = ({ mode = 'admin' }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const readOnly = mode !== 'admin';
  // i18n is portal-only — the hook gracefully falls back to English when
  // rendered outside the PortalI18nProvider (i.e. on /grant-policies/:id).
  const { t, lang, tDocType } = usePortalI18n();

  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState(null);
  // Per-slug sample-doc upload state.
  // Shape: { [slug]: bool } — true while a file is being uploaded.
  const [sampleUploading, setSampleUploading] = useState({});

  // Admin-editable mirror of the policy fields
  const [form, setForm] = useState({
    fiscal_year: '', title: '', description: '',
    eligible_amount: '', required_documents: '', status: 'active',
    required_documents_descriptions: {},
    required_documents_descriptions_hi: {},
  });

  const fetchPolicy = async () => {
    setLoading(true);
    try {
      const url = readOnly
        ? `${API}/portal/policies/${id}`
        : `${API}/grant-policies/${id}`;
      const res = await axios.get(url, { withCredentials: true });
      setPolicy(res.data);
      setForm({
        fiscal_year: res.data.fiscal_year || '',
        title: res.data.title || '',
        description: res.data.description || '',
        eligible_amount: res.data.eligible_amount ?? '',
        required_documents: (res.data.required_documents || []).join(', '),
        status: res.data.status || 'active',
        required_documents_descriptions: res.data.required_documents_descriptions || {},
        required_documents_descriptions_hi: res.data.required_documents_descriptions_hi || {},
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Policy not found');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPolicy(); /* eslint-disable-next-line */ }, [id]);

  // Keep the descriptions map in sync with the slug list — when the admin
  // adds a new slug, seed it with "" so the textarea below renders.
  const slugList = (form.required_documents || '')
    .split(',').map((s) => s.trim()).filter(Boolean);

  const save = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      // Only persist descriptions for slugs that are still in the list
      const trimmedDescs = {};
      const trimmedDescsHi = {};
      for (const slug of slugList) {
        const v = (form.required_documents_descriptions || {})[slug];
        if (v && v.trim()) trimmedDescs[slug] = v.trim();
        const vh = (form.required_documents_descriptions_hi || {})[slug];
        if (vh && vh.trim()) trimmedDescsHi[slug] = vh.trim();
      }
      await axios.put(`${API}/grant-policies/${id}`, {
        fiscal_year: form.fiscal_year,
        title: form.title,
        description: form.description,
        eligible_amount: Number(form.eligible_amount) || 0,
        required_documents: slugList,
        required_documents_descriptions: trimmedDescs,
        required_documents_descriptions_hi: trimmedDescsHi,
        status: form.status,
      }, { withCredentials: true });
      toast.success('Policy saved');
      fetchPolicy();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const validateDescriptions = async () => {
    // Persist any unsaved description tweaks before asking Gemini to grade
    // them — otherwise the AI would grade the LAST SAVED version, not what
    // the admin currently sees on screen, leading to confusing results.
    setValidating(true);
    setValidation(null);
    const t = toast.loading('Validating descriptions with AI…');
    try {
      const trimmedDescs = {};
      const trimmedDescsHi = {};
      for (const slug of slugList) {
        const v = (form.required_documents_descriptions || {})[slug];
        if (v && v.trim()) trimmedDescs[slug] = v.trim();
        const vh = (form.required_documents_descriptions_hi || {})[slug];
        if (vh && vh.trim()) trimmedDescsHi[slug] = vh.trim();
      }
      await axios.put(`${API}/grant-policies/${id}`, {
        required_documents: slugList,
        required_documents_descriptions: trimmedDescs,
        required_documents_descriptions_hi: trimmedDescsHi,
      }, { withCredentials: true });
      const res = await axios.post(
        `${API}/grant-policies/${id}/validate-descriptions`,
        {},
        { withCredentials: true, timeout: 60000 },
      );
      setValidation(res.data);
      toast.success(`AI verdict: ${res.data.overall_verdict} · ${res.data.overall_score}/100`, { id: t });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Validation failed', { id: t });
    } finally {
      setValidating(false);
    }
  };

  if (loading) {
    return (
      <div className="text-sm text-muted-foreground flex items-center gap-2 py-10">
        <Loader2 className="h-4 w-4 animate-spin" /> {readOnly ? t('policy.loadingPolicy') : 'Loading policy…'}
      </div>
    );
  }
  if (!policy) return null;

  const requiredDocs = (policy.required_documents || []);
  const samplesBySlug = policy.sample_documents || {};

  // Admin uploads a template file for a given slug. Reads the latest
  // policy back after success so the new sample appears in the list
  // without an extra round-trip cost on the user side.
  //
  // Guards against the common UX trap of typing a new slug in the
  // required-docs input → seeing the per-slug card appear → uploading
  // a sample BEFORE saving the policy. The backend correctly rejects
  // because the slug isn't in `required_documents` yet; here we catch
  // it first and show a clearer instruction.
  const uploadSample = async (slug, file, label) => {
    if (!file) return;
    const savedSlugs = (policy?.required_documents || []);
    if (!savedSlugs.includes(slug)) {
      toast.error(
        `Save the policy first — slug "${slug}" hasn't been persisted yet.`,
        { duration: 6000 },
      );
      return;
    }
    setSampleUploading((m) => ({ ...m, [slug]: true }));
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(
        `${API}/grant-policies/${id}/sample-documents`,
        fd,
        {
          params: { slug, label: label || '' },
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );
      toast.success(`Sample uploaded for ${slug}`);
      await fetchPolicy();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sample upload failed');
    } finally {
      setSampleUploading((m) => ({ ...m, [slug]: false }));
    }
  };

  const deleteSample = async (sampleId, filename) => {
    if (!window.confirm(`Remove sample "${filename}"?`)) return;
    try {
      await axios.delete(
        `${API}/grant-policies/${id}/sample-documents/${sampleId}`,
        { withCredentials: true }
      );
      toast.success('Sample deleted');
      await fetchPolicy();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete sample');
    }
  };

  // The download URL differs for admin vs secretary surfaces. Same blob
  // either way — backend gates the portal route to active policies only.
  const sampleDownloadUrl = (sampleId) =>
    readOnly
      ? `${API}/portal/policies/${id}/sample-documents/${sampleId}/download`
      : `${API}/grant-policies/${id}/sample-documents/${sampleId}/download`;

  return (
    <div className="space-y-5 max-w-4xl" data-testid="grant-policy-detail">
      <Button
        variant="ghost" size="sm"
        onClick={() => navigate(readOnly ? '/portal' : '/divisions')}
        className="-ml-2"
        data-testid="back-button"
      >
        <ArrowLeft className="h-4 w-4 mr-1.5" />
        {readOnly ? t('policy.backDashboard') : 'Back to MPCA Division Portal'}
      </Button>

      <div>
        <h1 className="text-2xl sm:text-3xl font-heading font-bold flex items-center gap-3">
          <ScrollText className="h-7 w-7 text-accent" />
          {readOnly ? policy.title : 'Edit Grant Policy'}
        </h1>
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <Badge variant="outline" className="text-xs">{policy.fiscal_year}</Badge>
          {policy.status === 'active' ? (
            <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
              {readOnly ? t('policy.statusActive') : 'Active'}
            </Badge>
          ) : (
            <Badge variant="secondary">{readOnly ? t('policy.statusClosed') : 'Closed'}</Badge>
          )}
          {readOnly && (
            <Badge variant="outline" className="text-xs gap-1 text-muted-foreground">
              <Lock className="h-3 w-3" /> {t('policy.readOnly')}
            </Badge>
          )}
        </div>
      </div>

      {/* Read-only view (secretary) ─────────────────────────────────────── */}
      {readOnly ? (
        <Card>
          <CardHeader className="border-b">
            <CardTitle className="text-base font-heading">{t('policy.schemeDetails')}</CardTitle>
          </CardHeader>
          <CardContent className="pt-5 space-y-5 text-sm">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">{t('policy.eligiblePerDivision')}</div>
                <div className="text-xl font-bold tabular-nums text-emerald-800 mt-1">
                  ₹{(policy.eligible_amount || 0).toLocaleString(lang === 'hi' ? 'hi-IN' : 'en-IN')}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">{t('policy.fiscalYear')}</div>
                <div className="mt-1 font-medium">{policy.fiscal_year}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">{t('policy.requiredDocs')}</div>
                <div className="mt-1 font-medium">{requiredDocs.length}</div>
              </div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">{t('policy.aboutScheme')}</div>
              <p className="whitespace-pre-wrap leading-relaxed">{policy.description || '—'}</p>
            </div>

            {requiredDocs.length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">{t('policy.docsToAttach')}</div>
                <ul className="space-y-3">
                  {requiredDocs.map((d) => {
                    const descsEn = policy.required_documents_descriptions || {};
                    const descsHi = policy.required_documents_descriptions_hi || {};
                    const desc = (lang === 'hi' ? (descsHi[d] || descsEn[d]) : descsEn[d]) || '';
                    const samples = samplesBySlug[d] || [];
                    return (
                      <li key={d} className="flex items-start gap-2 text-sm">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-600 mt-2 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="font-medium">{tDocType(d)}</div>
                          {desc && (
                            <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{desc}</p>
                          )}
                          {samples.length > 0 && (
                            <div className="mt-2 space-y-1">
                              <div className="text-[10px] uppercase tracking-wider text-emerald-800 font-semibold">
                                {t('policy.sampleToFillSign')}
                              </div>
                              <ul className="space-y-1">
                                {samples.map((s) => (
                                  <li
                                    key={s.id}
                                    className="flex items-center gap-2 px-2 py-1 rounded bg-emerald-50 border border-emerald-200 text-xs"
                                  >
                                    <FileText className="h-3 w-3 text-emerald-700" />
                                    <a
                                      href={sampleDownloadUrl(s.id)}
                                      target="_blank" rel="noreferrer"
                                      className="font-mono truncate flex-1 text-emerald-900 hover:underline"
                                      data-testid={`portal-sample-download-${s.id}`}
                                    >
                                      {s.filename}
                                    </a>
                                    <span className="text-[10px] text-muted-foreground tabular-nums">
                                      {s.size_bytes ? `${Math.round(s.size_bytes / 1024)} KB` : ''}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {policy.policy_document?.filename && (
              <div className="rounded-md border bg-muted/30 p-3 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 text-sm">
                  <FileText className="h-4 w-4 text-emerald-700" />
                  <span className="font-mono text-xs">{policy.policy_document.filename}</span>
                </div>
                <Button size="sm" variant="outline" asChild>
                  <a
                    href={`${API}/grant-policies/${policy.id}/document/download`}
                    target="_blank" rel="noreferrer"
                  >
                    {t('policy.downloadSchemePdf')}
                  </a>
                </Button>
              </div>
            )}

            <div className="pt-2 flex flex-wrap gap-2">
              <Button
                onClick={() => navigate(`/portal/claims?policy=${policy.id}`)}
                data-testid="start-claim-button"
              >
                <Layers className="h-4 w-4 mr-2" />
                {t('policy.startClaimAgainstScheme')}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        // Editable view (admin) ────────────────────────────────────────────
        <Card>
          <CardHeader className="border-b">
            <CardTitle className="text-base font-heading">Edit policy</CardTitle>
          </CardHeader>
          <CardContent className="pt-5">
            <form onSubmit={save} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Fiscal year *</Label>
                  <Input
                    required
                    value={form.fiscal_year}
                    onChange={(e) => setForm({ ...form, fiscal_year: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Eligible amount per division (₹)</Label>
                  <Input
                    type="number" min="0" step="1"
                    value={form.eligible_amount}
                    onChange={(e) => setForm({ ...form, eligible_amount: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Title *</Label>
                <Input
                  required
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Required documents <span className="text-xs text-muted-foreground">(comma-separated slugs, e.g. <code>proposal_letter, cost_estimate</code>)</span></Label>
                <Input
                  value={form.required_documents}
                  onChange={(e) => setForm({ ...form, required_documents: e.target.value })}
                />
              </div>
              {slugList.length > 0 && (
                <div className="space-y-2 rounded-md border bg-muted/20 p-3">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      What each document should contain
                      <span className="font-normal normal-case text-muted-foreground/80 ml-2">
                        (the AI uses these as a checklist when reviewing each claim)
                      </span>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={validateDescriptions}
                      disabled={validating}
                      data-testid="validate-descriptions-button"
                    >
                      {validating
                        ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Validating…</>
                        : <><Sparkles className="h-3.5 w-3.5 mr-1.5" />Validate against expectations</>
                      }
                    </Button>
                  </div>
                  {validation && (
                    <div
                      className={
                        'rounded-md border p-3 text-xs space-y-2 ' +
                        (validation.overall_verdict === 'ready'
                          ? 'border-emerald-300 bg-emerald-50'
                          : validation.overall_verdict === 'blocking'
                          ? 'border-rose-300 bg-rose-50'
                          : 'border-amber-300 bg-amber-50')
                      }
                      data-testid="validation-panel"
                    >
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <span className="font-semibold">
                          AI Verdict — {validation.overall_verdict} · {validation.overall_score}/100
                        </span>
                        <span className="text-[10px] text-muted-foreground">powered by Gemini 3.1 Pro</span>
                      </div>
                      {validation.summary && <p className="whitespace-pre-wrap">{validation.summary}</p>}
                      <div className="space-y-2 pt-1">
                        {(validation.per_slug || []).map((row) => (
                          <div key={row.slug} className="rounded bg-white/70 border p-2">
                            <div className="flex items-center justify-between flex-wrap gap-1.5">
                              <span className="font-mono text-xs font-semibold">{row.slug}</span>
                              <Badge
                                variant="outline"
                                className={
                                  row.verdict === 'good'
                                    ? 'border-emerald-400 text-emerald-700 text-[10px]'
                                    : row.verdict === 'weak'
                                    ? 'border-amber-400 text-amber-700 text-[10px]'
                                    : 'border-rose-400 text-rose-700 text-[10px]'
                                }
                              >
                                {row.verdict} · {row.score}/100
                              </Badge>
                            </div>
                            {(row.issues || []).length > 0 && (
                              <ul className="list-disc list-inside mt-1.5 text-[11px] text-muted-foreground">
                                {row.issues.map((iss, i) => <li key={i}>{iss}</li>)}
                              </ul>
                            )}
                            {row.suggestion && (
                              <p className="text-[11px] mt-1 italic text-foreground/80">
                                Suggested: {row.suggestion}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {slugList.map((slug) => {
                    // Visual + behavioural flag: this slug has NOT yet
                    // been persisted to `policy.required_documents`.
                    // Sample uploads are blocked until the admin clicks
                    // "Save policy".
                    const isUnsaved = !(policy?.required_documents || []).includes(slug);
                    return (
                    <div
                      key={slug}
                      className={`space-y-2 border rounded-md p-3 ${
                        isUnsaved ? 'bg-amber-50/40 border-amber-300' : 'bg-muted/20'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <Label className="text-xs font-mono text-foreground">{slug}</Label>
                        {isUnsaved && (
                          <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-400 gap-1">
                            <Lock className="h-2.5 w-2.5" />
                            Save policy first
                          </Badge>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <div className="space-y-0.5">
                          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                            English (default)
                          </div>
                          <Textarea
                            rows={2}
                            placeholder={`e.g. "Should include dimensions, materials and intended use of the ${slug.replace(/_/g, ' ')}"`}
                            value={(form.required_documents_descriptions || {})[slug] || ''}
                            onChange={(e) =>
                              setForm({
                                ...form,
                                required_documents_descriptions: {
                                  ...(form.required_documents_descriptions || {}),
                                  [slug]: e.target.value,
                                },
                              })
                            }
                            data-testid={`doc-desc-${slug}`}
                          />
                        </div>
                        <div className="space-y-0.5">
                          <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                            <span className="font-semibold">हिन्दी</span>
                            <span className="opacity-60">— shown when secretary picks हिन्दी</span>
                          </div>
                          <Textarea
                            rows={2}
                            placeholder="वैकल्पिक — कृपया हिंदी में बताएँ कि इसमें क्या होना चाहिए (कैलकुलेशन, हस्ताक्षर, मुहर, इत्यादि)"
                            value={(form.required_documents_descriptions_hi || {})[slug] || ''}
                            onChange={(e) =>
                              setForm({
                                ...form,
                                required_documents_descriptions_hi: {
                                  ...(form.required_documents_descriptions_hi || {}),
                                  [slug]: e.target.value,
                                },
                              })
                            }
                            data-testid={`doc-desc-hi-${slug}`}
                            style={{ fontFeatureSettings: '"hlig", "calt"' }}
                          />
                        </div>
                      </div>

                      {/* Sample / template uploader. Admin pushes a blank
                          form here; the secretary downloads it, fills,
                          signs, and re-uploads as their submission for
                          this slug. The AI compares structure on review. */}
                      <div className="rounded-md border border-dashed bg-white p-2.5 space-y-2">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1.5">
                              <FileText className="h-3 w-3" />
                              Sample / template documents
                            </div>
                            <div className="text-[10px] text-muted-foreground">
                              Division will download, fill, sign, and re-upload.
                            </div>
                          </div>
                          <label>
                            <Button asChild size="sm" variant="outline" disabled={!!sampleUploading[slug]} data-testid={`sample-upload-btn-${slug}`}>
                              <span className="cursor-pointer">
                                <Upload className={`h-3.5 w-3.5 mr-1.5 ${sampleUploading[slug] ? 'animate-pulse' : ''}`} />
                                {sampleUploading[slug] ? 'Uploading…' : 'Add sample'}
                              </span>
                            </Button>
                            <input
                              type="file"
                              className="hidden"
                              accept="application/pdf,image/*,.doc,.docx"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) uploadSample(slug, file);
                                e.target.value = '';
                              }}
                              data-testid={`sample-upload-input-${slug}`}
                            />
                          </label>
                        </div>
                        {(samplesBySlug[slug] || []).length === 0 ? (
                          <div className="text-[11px] text-muted-foreground italic py-1">
                            No sample uploaded yet — secretary will only see the description above.
                          </div>
                        ) : (
                          <ul className="space-y-1">
                            {(samplesBySlug[slug] || []).map((s) => (
                              <li
                                key={s.id}
                                className="flex items-center gap-2 px-2 py-1 rounded bg-muted/40 text-xs"
                                data-testid={`sample-row-${s.id}`}
                              >
                                <FileText className="h-3 w-3 text-muted-foreground" />
                                <a
                                  href={sampleDownloadUrl(s.id)}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="font-mono truncate flex-1 hover:underline"
                                >
                                  {s.filename}
                                </a>
                                <span className="text-[10px] text-muted-foreground tabular-nums">
                                  {s.size_bytes ? `${Math.round(s.size_bytes / 1024)} KB` : ''}
                                </span>
                                <Button
                                  size="sm" variant="ghost"
                                  className="h-6 w-6 p-0"
                                  onClick={() => deleteSample(s.id, s.filename)}
                                  title="Remove sample"
                                  data-testid={`sample-delete-${s.id}`}
                                >
                                  <Trash2 className="h-3.5 w-3.5 text-rose-600" />
                                </Button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
              <div className="space-y-1.5">
                <Label>Description</Label>
                <Textarea
                  rows={8}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </div>
              <div className="flex items-center justify-between rounded-md bg-muted/30 border p-3">
                <div>
                  <Label className="cursor-pointer">Publish to portal</Label>
                  <p className="text-xs text-muted-foreground">When off, divisions will not see this policy.</p>
                </div>
                <Switch
                  checked={form.status === 'active'}
                  onCheckedChange={(c) => setForm({ ...form, status: c ? 'active' : 'closed' })}
                />
              </div>
              {policy.policy_document?.filename && (
                <div className="rounded-md border bg-muted/30 p-3 flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 text-sm">
                    <FileText className="h-4 w-4 text-emerald-700" />
                    <span className="font-mono text-xs">{policy.policy_document.filename}</span>
                  </div>
                  <Button size="sm" variant="outline" asChild>
                    <a
                      href={`${API}/grant-policies/${policy.id}/document/download`}
                      target="_blank" rel="noreferrer"
                    >
                      Download
                    </a>
                  </Button>
                </div>
              )}
              <div className="flex gap-2 justify-end pt-2">
                <Button
                  type="button" variant="outline"
                  onClick={() => navigate(`/grant-claims?policy=${policy.id}`)}
                  data-testid="view-claims-button"
                >
                  <Layers className="h-4 w-4 mr-1.5" />
                  View claims
                </Button>
                <Button type="submit" disabled={saving} data-testid="save-policy-button">
                  {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
                  Save changes
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default GrantPolicyDetail;
